import type { CategoryComparisonRow } from './comparisonBarTypes';

/**
 * Row order for the comparison chart: biggest current-range value first.
 *
 * The one ordering this chart has. There is no sort control and no alternative option — the design
 * gives the section band no header cells to click, and largest-first is what makes the bar lengths
 * read as a ranking down the column rather than as an arbitrary sequence.
 *
 * The primary range is the report the user asked for; comparison ranges provide context for it but
 * do not reorder it. Sorting by a comparison value made the ranking jump when comparison was
 * enabled and could put a current $0 row above every category with current activity.
 *
 * Magnitude, not signed value, so an expense section (whose values are negative) ranks the same way
 * an income section does. Ties fall back to the name so the order is stable across re-renders —
 * `Array.prototype.sort` is stable, but the row input order is not something the model promises.
 *
 * Note this is also the order truncation cuts on: the rollup keeps the first N current-range rows,
 * so "top 8 plus Everything else" is a consequence of sorting here rather than a second rule.
 */
/**
 * The sort key, exported because the rollup selects which rows survive truncation by the same
 * measure. Two independent definitions would let a visible row sit below a rolled-up one.
 */
export const currentComparisonBar = (row: CategoryComparisonRow): number => Math.abs(row.values[0] ?? 0);

/** Widest bar across every range, used for chart scale calculation rather than row ordering. */
export const longestComparisonBar = (row: CategoryComparisonRow): number =>
  row.values.reduce<number>((widest, value) => Math.max(widest, Math.abs(value ?? 0)), 0);

/** Shared comparator for display ordering and top-N rollup selection. */
export const compareComparisonBarRows = (a: CategoryComparisonRow, b: CategoryComparisonRow): number => {
  const difference = currentComparisonBar(b) - currentComparisonBar(a);
  if (difference !== 0) return difference;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
};

export const sortComparisonBarRows = <R extends CategoryComparisonRow>(rows: R[]): R[] =>
  [...rows].sort(compareComparisonBarRows);
